// src/services/whitelist.service.ts

import { supabase } from "./supabase";

export interface WhitelistEntry {
  id: string;
  email: string;
  discount_percentage: number;
  reason: string | null;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
  is_active: boolean;
  usage_count: number;
  max_uses: number | null;
  updated_at: string | null;
}

class WhitelistService {
  private readonly TABLE = "whitelisted_emails";
  private cache: Map<string, WhitelistEntry | null> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private cacheTimestamps: Map<string, number> = new Map();

  /**
   * Check if an email is whitelisted and get discount info
   */
  async checkEmail(email: string): Promise<{
    isWhitelisted: boolean;
    discountPercentage: number;
    entry: WhitelistEntry | null;
  }> {
    try {
      console.log("🔍 [WhitelistService] Checking email:", email);

      // Get current session to verify auth
      const { data: session } = await supabase.auth.getSession();
      console.log("🔍 [WhitelistService] Current session:", {
        hasSession: !!session?.session,
        userEmail: session?.session?.user?.email,
        userId: session?.session?.user?.id,
      });

      // Normalize email (lowercase)
      const normalizedEmail = email.toLowerCase().trim();
      console.log("🔍 [WhitelistService] Normalized email:", normalizedEmail);

      // Check cache first
      const cached = this.getFromCache(normalizedEmail);
      if (cached !== undefined) {
        console.log("🔍 [WhitelistService] Cache hit:", cached);
        return {
          isWhitelisted: cached !== null,
          discountPercentage: cached?.discount_percentage || 0,
          entry: cached,
        };
      }
      console.log("🔍 [WhitelistService] Cache miss");

      // Query database
      console.log("🔍 [WhitelistService] Querying database for:", normalizedEmail);

      // Try with maybeSingle first
      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("email", normalizedEmail)
        .eq("is_active", true)
        .maybeSingle();

      if (error) {
        console.error("❌ [WhitelistService] Error checking whitelist:", error);
        return { isWhitelisted: false, discountPercentage: 0, entry: null };
      }

      console.log("🔍 [WhitelistService] Database result:", data);

      // If data is null, try without the is_active filter to see if entry exists but is inactive
      if (!data) {
        const { data: inactiveData } = await supabase
          .from(this.TABLE)
          .select("*")
          .eq("email", normalizedEmail)
          .maybeSingle();

        if (inactiveData) {
          console.log("🔍 [WhitelistService] Found inactive entry:", inactiveData);
        }
      }

      // Type assertion since we know the structure
      const whitelistData = data as WhitelistEntry | null;

      // Check expiration
      if (whitelistData?.expires_at) {
        console.log("🔍 [WhitelistService] Checking expiration:", {
          expires_at: whitelistData.expires_at,
          now: new Date().toISOString(),
          isExpired: new Date(whitelistData.expires_at) < new Date(),
        });
        if (new Date(whitelistData.expires_at) < new Date()) {
          console.log("🔍 [WhitelistService] Entry expired");
          this.addToCache(normalizedEmail, null);
          return { isWhitelisted: false, discountPercentage: 0, entry: null };
        }
      }

      // Check usage limits
      if (whitelistData?.max_uses) {
        console.log("🔍 [WhitelistService] Checking usage limits:", {
          usage_count: whitelistData.usage_count,
          max_uses: whitelistData.max_uses,
        });
        if (whitelistData.usage_count >= whitelistData.max_uses) {
          console.log("🔍 [WhitelistService] Usage limit exceeded");
          this.addToCache(normalizedEmail, null);
          return { isWhitelisted: false, discountPercentage: 0, entry: null };
        }
      }

      // Cache and return
      console.log(
        "✅ [WhitelistService] Email is whitelisted with discount:",
        whitelistData?.discount_percentage
      );
      this.addToCache(normalizedEmail, whitelistData);
      return {
        isWhitelisted: !!whitelistData,
        discountPercentage: whitelistData?.discount_percentage || 0,
        entry: whitelistData,
      };
    } catch (error) {
      console.error("❌ [WhitelistService] Error in checkEmail:", error);
      return { isWhitelisted: false, discountPercentage: 0, entry: null };
    }
  }

  /**
   * Increment usage count for a whitelist entry
   */
  async incrementUsage(email: string): Promise<void> {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      // Update in background - don't await to avoid blocking
      supabase
        .rpc("increment_whitelist_usage", {
          p_email: normalizedEmail,
        })
        .then(({ error }) => {
          if (error) console.error("Error incrementing usage:", error);
        });

      // Invalidate cache
      this.invalidateCache(normalizedEmail);
    } catch (error) {
      console.error("Error incrementing usage:", error);
    }
  }

  /**
   * Add email to whitelist (admin function)
   */
  async addToWhitelist(
    email: string,
    discountPercentage: number = 100,
    options?: {
      reason?: string;
      expiresAt?: string;
      maxUses?: number;
      createdBy?: string;
    }
  ): Promise<boolean> {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { error } = await supabase.from(this.TABLE).insert({
        email: normalizedEmail,
        discount_percentage: discountPercentage,
        reason: options?.reason,
        expires_at: options?.expiresAt,
        max_uses: options?.maxUses,
        created_by: options?.createdBy,
        is_active: true,
        usage_count: 0,
        updated_at: new Date().toISOString(),
      });

      if (error) {
        console.error("Error adding to whitelist:", error);
        return false;
      }

      // Invalidate cache
      this.invalidateCache(normalizedEmail);
      return true;
    } catch (error) {
      console.error("Error in addToWhitelist:", error);
      return false;
    }
  }

  /**
   * Remove email from whitelist (admin function)
   */
  async removeFromWhitelist(email: string): Promise<boolean> {
    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { error } = await supabase
        .from(this.TABLE)
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq("email", normalizedEmail);

      if (error) {
        console.error("Error removing from whitelist:", error);
        return false;
      }

      // Invalidate cache
      this.invalidateCache(normalizedEmail);
      return true;
    } catch (error) {
      console.error("Error in removeFromWhitelist:", error);
      return false;
    }
  }

  /**
   * Bulk import emails to whitelist (admin function)
   */
  async bulkImport(
    emails: Array<{
      email: string;
      discountPercentage?: number;
      reason?: string;
      maxUses?: number;
    }>
  ): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;

    for (const item of emails) {
      const result = await this.addToWhitelist(item.email, item.discountPercentage || 100, {
        reason: item.reason,
        maxUses: item.maxUses,
      });
      if (result) success++;
      else failed++;
    }

    return { success, failed };
  }

  // Cache management
  private getFromCache(email: string): WhitelistEntry | null | undefined {
    const timestamp = this.cacheTimestamps.get(email);
    if (timestamp && Date.now() - timestamp < this.cacheTimeout) {
      return this.cache.get(email);
    }
    return undefined;
  }

  private addToCache(email: string, entry: WhitelistEntry | null): void {
    this.cache.set(email, entry);
    this.cacheTimestamps.set(email, Date.now());
  }

  private invalidateCache(email: string): void {
    this.cache.delete(email);
    this.cacheTimestamps.delete(email);
  }
}

export const whitelistService = new WhitelistService();
